import { z } from "zod";
import type { HandleType, UIComponentType } from "../../ai/parameter-schema";

/**
 * Common AI Parameter UI Schema
 *
 * Defines UI standards for the 28 most common AI model parameters (usage ≥ 50 across 807 models).
 * These definitions are merged with model-specific schemas at runtime to create adaptive UI.
 *
 * @see docs/ai/PARAMETER_UI_SYSTEM.md for full documentation
 * @package @genieartinc/schemas
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Extended component types (alias for UIComponentType which now includes all variants)
 */
export type GenieUIComponentType = UIComponentType;

export type ParameterCategory = "generic" | "adaptive" | "input" | "output";

/**
 * Parameter Control Policy
 *
 * Defines platform-level controls for parameters (e.g., safety, compliance, limits)
 */
export interface ParameterControlPolicy {
  /** How the parameter should be displayed */
  visibility: "visible" | "hidden" | "readonly";

  /** Whether user can edit this parameter */
  userEditable: boolean;

  /** Platform-enforced default value (overrides user choice) */
  platformDefault?: unknown;

  /** Explanation for why parameter is locked/restricted */
  lockReason?: string;
}

export interface GenieParameterUI {
  name: string;
  label: string;
  description: string;
  category: ParameterCategory;

  // Type (can be "adaptive" for parameters that change based on model)
  type: "string" | "integer" | "number" | "boolean" | "array" | "adaptive" | "output";

  // UI Component
  component?: GenieUIComponentType;

  // Default values and ranges (wide ranges for flexibility)
  defaultRange?: { min: number; max: number };
  commonValues?: unknown[];
  defaultValue?: unknown;

  // Constraints
  maxLength?: number;
  required?: boolean;

  // Node input/output configuration
  isNodeInput?: boolean;
  handleType?: HandleType;

  // Platform control policy (optional - for locked/restricted parameters)
  policy?: ParameterControlPolicy;
}

// ============================================================================
// COMMON PARAMETERS UI DEFINITIONS
// ============================================================================

export const CommonAiParametersUI: Record<string, GenieParameterUI> = {
  // ==========================================================================
  // GENERIC PARAMETERS (Single variant, high usage)
  // ==========================================================================

  seed: {
    name: "seed",
    label: "Seed",
    description: "Random seed for reproducibility. Use same seed for consistent results.",
    category: "generic",
    type: "integer",
    component: "number",
    defaultRange: { min: 0, max: 2147483647 },
    defaultValue: 0, // 0 = random seed
  },

  prompt: {
    name: "prompt",
    label: "Prompt",
    description: "Text description for generation. Be specific and detailed.",
    category: "generic",
    type: "string",
    component: "textarea",
    maxLength: 5000,
    required: true,
  },

  negative_prompt: {
    name: "negative_prompt",
    label: "Negative Prompt",
    description: "Describe what to avoid in generation (e.g., 'blurry, distorted').",
    category: "generic",
    type: "string",
    component: "textarea",
    maxLength: 5000,
  },

  enable_safety_checker: {
    name: "enable_safety_checker",
    label: "Safety Checker",
    description: "Enable NSFW content filtering",
    category: "generic",
    type: "boolean",
    component: "toggle",
    defaultValue: true,

    // 🔒 Platform Policy: Safety checker is mandatory
    policy: {
      visibility: "readonly",
      userEditable: false,
      platformDefault: true,
      lockReason: "Platform safety requirement - NSFW content filtering is mandatory",
    },
  },

  limit_generations: {
    name: "limit_generations",
    label: "Limit Generations",
    description: "Return one image per request, ignoring prompt instructions to generate more",
    category: "generic",
    type: "boolean",
    component: "toggle",
    defaultValue: true,

    // 🔒 Platform Policy: one output per billed variant. Gemini / nano-banana models return
    // extra images when this is false, which would hand back more output than was paid for.
    policy: {
      visibility: "readonly",
      userEditable: false,
      platformDefault: true,
      lockReason: "Billing requirement - each generation is charged per output, so extra outputs are disabled",
    },
  },

  sync_mode: {
    name: "sync_mode",
    label: "Sync Mode",
    description: "Wait for completion (true) or run asynchronously (false)",
    category: "generic",
    type: "boolean",
    component: "toggle",
    defaultValue: true,
  },

  expand_prompt: {
    name: "expand_prompt",
    label: "Expand Prompt",
    description: "Automatically enhance and expand the prompt for better results",
    category: "generic",
    type: "boolean",
    component: "toggle",
    defaultValue: false,
  },

  // ==========================================================================
  // LLM PARAMETERS (Text/language model generation controls)
  // ==========================================================================

  temperature: {
    name: "temperature",
    label: "Temperature",
    description: "Controls randomness. Lower = more focused and deterministic, higher = more creative.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: 0, max: 2 },
    defaultValue: 0.7,
  },

  max_tokens: {
    name: "max_tokens",
    label: "Max Tokens",
    description: "Maximum number of tokens to generate in the response.",
    category: "generic",
    type: "integer",
    component: "number",
    defaultRange: { min: 1, max: 128000 },
    defaultValue: 4096,
  },

  top_p: {
    name: "top_p",
    label: "Top P",
    description: "Nucleus sampling: only consider tokens within this cumulative probability. Lower = more focused.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: 0, max: 1 },
    defaultValue: 1,
  },

  top_k: {
    name: "top_k",
    label: "Top K",
    description: "Limits token selection to top K candidates. 0 = disabled (no limit).",
    category: "generic",
    type: "integer",
    component: "number",
    defaultRange: { min: 0, max: 500 },
    defaultValue: 0,
  },

  frequency_penalty: {
    name: "frequency_penalty",
    label: "Frequency Penalty",
    description: "Penalizes repeated tokens based on frequency. Positive = less repetition.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: -2, max: 2 },
    defaultValue: 0,
  },

  presence_penalty: {
    name: "presence_penalty",
    label: "Presence Penalty",
    description: "Penalizes tokens that have already appeared. Positive = encourages new topics.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: -2, max: 2 },
    defaultValue: 0,
  },

  repetition_penalty: {
    name: "repetition_penalty",
    label: "Repetition Penalty",
    description: "Penalizes repeated sequences. 1.0 = no penalty, higher = less repetition.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: 0, max: 2 },
    defaultValue: 1,
  },

  min_p: {
    name: "min_p",
    label: "Min P",
    description: "Minimum probability threshold. Tokens below this probability are filtered out. Alternative to top_p.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: 0, max: 1 },
    defaultValue: 0,
  },

  top_a: {
    name: "top_a",
    label: "Top A",
    description: "Top-A sampling threshold. Filters tokens whose probability is below top_a * max_probability. 0 = disabled.",
    category: "generic",
    type: "number",
    component: "slider",
    defaultRange: { min: 0, max: 1 },
    defaultValue: 0,
  },

  // ==========================================================================
  // ADAPTIVE PARAMETERS (Multiple variants, model-specific constraints)
  // ==========================================================================

  num_inference_steps: {
    name: "num_inference_steps",
    label: "Inference Steps",
    description: "Number of denoising steps. More steps = higher quality but slower.",
    category: "adaptive",
    type: "adaptive",
    component: "slider",
    defaultRange: { min: 1, max: 150 },
    commonValues: [20, 28, 35, 50, 75, 100],
    defaultValue: 28,
  },

  guidance_scale: {
    name: "guidance_scale",
    label: "Guidance Scale",
    description: "How closely to follow the prompt. Higher = more literal interpretation.",
    category: "adaptive",
    type: "adaptive",
    component: "slider",
    defaultRange: { min: 0, max: 20 },
    commonValues: [3.5, 5, 7.5, 10, 15],
    defaultValue: 7.5,
  },

  aspect_ratio: {
    name: "aspect_ratio",
    label: "Aspect Ratio",
    description: "Output image/video dimensions ratio",
    category: "adaptive",
    type: "adaptive",
    component: "select-or-input",
    commonValues: ["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"],
    defaultValue: "16:9",
  },

  num_images: {
    name: "num_images",
    label: "Number of Images",
    description: "How many images to generate in one request",
    category: "adaptive",
    type: "adaptive",
    component: "number",
    defaultRange: { min: 1, max: 10 },
    defaultValue: 1,
  },

  output_format: {
    name: "output_format",
    label: "Output Format",
    description: "Image or video file format",
    category: "adaptive",
    type: "adaptive",
    component: "select",
    commonValues: ["png", "jpeg", "webp", "mp4", "gif"],
    defaultValue: "png",
  },

  image_size: {
    name: "image_size",
    label: "Image Size",
    description: "Output resolution preset",
    category: "adaptive",
    type: "adaptive",
    component: "select",
    commonValues: ["square", "square_hd", "landscape_4_3", "portrait_4_3", "landscape_16_9", "portrait_16_9"],
    defaultValue: "square_hd",
  },

  resolution: {
    name: "resolution",
    label: "Resolution",
    description: "Output video/image resolution",
    category: "adaptive",
    type: "adaptive",
    component: "select",
    commonValues: ["480p", "720p", "1080p", "2k", "4k"],
    defaultValue: "1080p",
  },

  duration: {
    name: "duration",
    label: "Duration",
    description: "Video duration in seconds",
    category: "adaptive",
    type: "adaptive",
    component: "select-or-input",
    commonValues: [5, 6, 7, 8, 9, 10],
    defaultValue: 10,
  },

  num_frames: {
    name: "num_frames",
    label: "Number of Frames",
    description: "Total frames to generate (video/animation)",
    category: "adaptive",
    type: "adaptive",
    component: "slider",
    defaultRange: { min: 1, max: 300 },
    defaultValue: 60,
  },

  strength: {
    name: "strength",
    label: "Strength",
    description: "How much to transform the input. 0 = no change, 1 = maximum transformation.",
    category: "adaptive",
    type: "adaptive",
    component: "slider",
    defaultRange: { min: 0, max: 1 },
    defaultValue: 0.8,
  },

  acceleration: {
    name: "acceleration",
    label: "Acceleration",
    description: "Speed up generation (may reduce quality)",
    category: "adaptive",
    type: "adaptive",
    component: "select",
    commonValues: ["none", "fast", "ultra"],
    defaultValue: "none",
  },

  loras: {
    name: "loras",
    label: "LoRA Models",
    description: "Additional style models to apply (Low-Rank Adaptation)",
    category: "adaptive",
    type: "array",
    component: "multi-select",
    defaultValue: [],
  },

  style: {
    name: "style",
    label: "Style",
    description: "Artistic style preset",
    category: "adaptive",
    type: "adaptive",
    component: "select",
    commonValues: ["realistic", "anime", "artistic", "3d", "photographic", "cinematic", "illustration"],
    defaultValue: "realistic",
  },

  has_nsfw_concepts: {
    name: "has_nsfw_concepts",
    label: "NSFW Detected",
    description: "Whether NSFW content was detected (output only)",
    category: "adaptive",
    type: "adaptive",
    component: undefined, // Output only, not for UI
  },

  timings: {
    name: "timings",
    label: "Processing Timings",
    description: "Performance metrics (output only)",
    category: "adaptive",
    type: "adaptive",
    component: undefined, // Output only, not for UI
  },

  // ==========================================================================
  // INPUT PARAMETERS (Node handles - media inputs)
  // ==========================================================================

  image_url: {
    name: "image_url",
    label: "Input Image",
    description: "URL or path to input image",
    category: "input",
    type: "string",
    isNodeInput: true,
    handleType: "image",
  },

  video_url: {
    name: "video_url",
    label: "Input Video",
    description: "URL or path to input video",
    category: "input",
    type: "string",
    isNodeInput: true,
    handleType: "video",
  },

  audio_url: {
    name: "audio_url",
    label: "Input Audio",
    description: "URL or path to input audio",
    category: "input",
    type: "string",
    isNodeInput: true,
    handleType: "audio",
  },

  // ==========================================================================
  // OUTPUT PARAMETERS (Node handles - generated media)
  // ==========================================================================

  video: {
    name: "video",
    label: "Video Output",
    description: "Generated video file",
    category: "output",
    type: "output",
    handleType: "video",
  },

  images: {
    name: "images",
    label: "Images Output",
    description: "Array of generated images",
    category: "output",
    type: "output",
    handleType: "image",
  },

  image: {
    name: "image",
    label: "Image Output",
    description: "Single generated image",
    category: "output",
    type: "output",
    handleType: "image",
  },

  audio: {
    name: "audio",
    label: "Audio Output",
    description: "Generated audio file",
    category: "output",
    type: "output",
    handleType: "audio",
  },
};

// ============================================================================
// ZOD SCHEMAS FOR VALIDATION
// ============================================================================

export const ParameterControlPolicySchema = z.object({
  visibility: z.enum(["visible", "hidden", "readonly"]),
  userEditable: z.boolean(),
  platformDefault: z.unknown().optional(),
  lockReason: z.string().optional(),
});

export const GenieParameterUISchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.enum(["generic", "adaptive", "input", "output"]),
  type: z.enum(["string", "integer", "number", "boolean", "array", "adaptive", "output"]),
  component: z.enum([
    "slider",
    "select",
    "input",
    "textarea",
    "toggle",
    "number",
    "file",
    "color",
    "select-or-input",
    "multi-select",
  ]).optional(),
  defaultRange: z.object({
    min: z.number(),
    max: z.number(),
  }).optional(),
  commonValues: z.array(z.unknown()).optional(),
  defaultValue: z.unknown().optional(),
  maxLength: z.number().optional(),
  required: z.boolean().optional(),
  isNodeInput: z.boolean().optional(),
  handleType: z.enum(["image", "video", "audio", "file", "mask"]).optional(),
  policy: ParameterControlPolicySchema.optional(),
});

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get parameter UI definition by name
 */
export function getParameterUI(paramName: string): GenieParameterUI | undefined {
  return CommonAiParametersUI[paramName];
}

/**
 * Check if a parameter is a common parameter
 */
export function isCommonParameter(paramName: string): boolean {
  return paramName in CommonAiParametersUI;
}

/**
 * Get all parameters by category
 */
export function getParametersByCategory(category: ParameterCategory): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(p => p.category === category);
}

/**
 * Get all node input parameters
 */
export function getNodeInputParameters(): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(p => p.isNodeInput === true);
}

/**
 * Get all output parameters
 */
export function getOutputParameters(): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(p => p.category === "output");
}

/**
 * Get all settings panel parameters (excludes inputs/outputs)
 */
export function getSettingsPanelParameters(): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(
    p => p.category !== "input" && p.category !== "output" && p.component !== undefined
  );
}

// ============================================================================
// POLICY HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a parameter has a policy
 */
export function hasPolicy(paramName: string): boolean {
  const param = getParameterUI(paramName);
  return param?.policy !== undefined;
}

/**
 * Check if a parameter is locked (not user editable)
 */
export function isParameterLocked(paramName: string): boolean {
  const param = getParameterUI(paramName);
  return param?.policy?.userEditable === false;
}

/**
 * Check if parameter should be hidden
 */
export function isParameterHidden(paramName: string): boolean {
  const param = getParameterUI(paramName);
  return param?.policy?.visibility === "hidden";
}

/**
 * Check if parameter should be readonly
 */
export function isParameterReadonly(paramName: string): boolean {
  const param = getParameterUI(paramName);
  return param?.policy?.visibility === "readonly";
}

/**
 * Get platform default value for a parameter
 */
export function getPlatformDefault(paramName: string): unknown {
  const param = getParameterUI(paramName);
  return param?.policy?.platformDefault;
}

/**
 * Get effective value (platform default if locked, otherwise user value)
 */
export function getEffectiveValue(paramName: string, userValue: unknown): unknown {
  const param = getParameterUI(paramName);

  // If parameter has platform default and is not user editable, use platform default
  if (param?.policy?.platformDefault !== undefined && param.policy.userEditable === false) {
    return param.policy.platformDefault;
  }

  // Otherwise use user value
  return userValue;
}

/**
 * Get all locked parameters
 */
export function getLockedParameters(): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(
    p => p.policy?.userEditable === false
  );
}

/**
 * Get all parameters with policies
 */
export function getParametersWithPolicies(): GenieParameterUI[] {
  return Object.values(CommonAiParametersUI).filter(p => p.policy !== undefined);
}

// ============================================================================
// STATISTICS
// ============================================================================

/**
 * Statistics about common parameters
 */
export const CommonParameterStats = {
  total: Object.keys(CommonAiParametersUI).length,
  generic: getParametersByCategory("generic").length,
  adaptive: getParametersByCategory("adaptive").length,
  inputs: getNodeInputParameters().length,
  outputs: getOutputParameters().length,
  settingsPanelParams: getSettingsPanelParameters().length,
  withPolicies: getParametersWithPolicies().length,
  locked: getLockedParameters().length,
} as const;
